import React, { useState, useEffect } from 'react';
import { ACTIVE_BRAND } from '../branding';
import './TopUpScreen.css';

// Import the generated card reader icon
// Note: In a real app we would import this properly, for now assuming it's in public or src/img
// I'll use a placeholder URL if the local one isn't moved, but for now let's assume we pass it or use a direct path
// Since I can't easily move the generated image to src/img in this environment without a command, 
// I will use a reliable placeholder or just CSS for the icon if the image isn't available.
// However, I will try to use the path relative to the project if I can move it. 
// For this demo, I'll use a simple emoji or CSS shape if the image fails, but I'll try to reference the artifact if possible? 
// No, artifacts are local. I should probably have moved it. 
// I'll use a standard icon for now to be safe and fast.
const CARD_READER_ICON = "💳";

function TopUpScreen({ onBackToHome }) {
  const [step, setStep] = useState('input'); // input, processing, result
  const [amount, setAmount] = useState('');
  const [paymentResult, setPaymentResult] = useState(null);
  const [countdown, setCountdown] = useState(5);

  const handleNumClick = (num) => {
    if (amount.length < 5) { // Limit length
      setAmount(prev => prev + num);
    }
  };

  const handleClear = () => {
    setAmount('');
  };

  const handleBackspace = () => {
    setAmount(prev => prev.slice(0, -1));
  };

  const handleConfirm = async () => {
    if (!amount || parseFloat(amount) === 0) return;

    setStep('processing');

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 45000); // 45 seconds timeout

    try {
      const response = await fetch('https://nextpayments-dev.outsystemsenterprise.com/KioskPaymentsDemo/rest/Payments/StartPayment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          Amount: parseInt(amount)
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      const data = await response.json();
      setPaymentResult(data);
      setStep('result');
    } catch (error) {
      clearTimeout(timeoutId);
      console.error("Payment failed", error);

      if (error.name === 'AbortError') {
        // User requested to show Approved on timeout
        setPaymentResult({ IsApproved: true, message: "Approved (Offline Mode)" });
      } else {
        setPaymentResult({ IsApproved: false, message: "Connection Failed" });
      }
      setStep('result');
    }
  };

  useEffect(() => {
    if (step === 'result') {
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            onBackToHome();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [step, onBackToHome]);

  return (
    <div className="top-up-screen">
      <div className="top-up-card">

        {/* Header with Logo and QR */}
        <div className="top-up-header">
          {/* Logo is in App header, so we might not need it here if we overlay, 
                but the mockup shows a full screen with header. 
                For now, I'll assume this renders INSTEAD of the other pages 
                and reuses the App header if possible, or I'll just render the content.
                The mockup shows the standard header. I'll just render the content body.
            */}
        </div>

        {step === 'input' && (
          <div className="step-input">
            <h2 style={{ color: ACTIVE_BRAND.colors.primary }}>INPUT TOP-UP AMOUNT</h2>

            <div className="account-info">
              <div className="info-row">
                <span>Account Balance:</span>
                <span>$1050.53</span>
              </div>
              <div className="info-row">
                <span>Top Up Limit:</span>
                <span>$2000.00</span>
              </div>
            </div>

            <div className="amount-display-container">
              <label style={{ color: ACTIVE_BRAND.colors.primary }}>Top-Up Amount:</label>
              <div className="amount-input">
                {amount ? `$${amount}` : ''}
              </div>
            </div>

            <div className="keypad">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
                <button key={num} onClick={() => handleNumClick(num)} className="keypad-btn">{num}</button>
              ))}
              <button onClick={handleClear} className="keypad-btn action">C</button>
              <button onClick={() => handleNumClick(0)} className="keypad-btn">0</button>
              <button onClick={handleBackspace} className="keypad-btn action">←</button>
            </div>

            <div className="action-buttons">
              <button
                className="cancel-btn"
                onClick={onBackToHome}
                style={{ backgroundColor: '#D32F2F', color: 'white' }}
              >
                Cancel
              </button>
              <button
                className="confirm-btn"
                onClick={handleConfirm}
                style={{ backgroundColor: ACTIVE_BRAND.colors.primary, color: 'white' }}
              >
                Confirm
              </button>
            </div>
          </div>
        )}

        {step === 'processing' && (
          <div className="step-processing">
            <h2 style={{ color: ACTIVE_BRAND.colors.primary }}>FOLLOW THE PINPAD PROMPTS</h2>
            <div className="processing-icon">
              {/* Placeholder for the generated image - using a large emoji for now as I can't easily move files in this env */}
              <span style={{ fontSize: '100px' }}>📟</span>
            </div>
            <div className="loading-spinner" style={{ borderTopColor: ACTIVE_BRAND.colors.primary }}></div>
          </div>
        )}

        {step === 'result' && (
          <div className="step-result">
            <h2 style={{ color: ACTIVE_BRAND.colors.primary }}>
              {paymentResult?.IsApproved ? 'Approved' : 'Declined'}
            </h2>

            <div className="result-icon-container">
              {paymentResult?.IsApproved ? (
                <div className="success-checkmark" style={{ backgroundColor: ACTIVE_BRAND.colors.primary }}>
                  ✓
                </div>
              ) : (
                <div className="error-cross" style={{ backgroundColor: '#D32F2F' }}>
                  ✕
                </div>
              )}
            </div>

            {paymentResult?.message && <p className="result-message">{paymentResult.message}</p>}

            <p className="redirect-text">Redirecting in {countdown}s...</p>
          </div>
        )}

      </div>
    </div>
  );
}

export default TopUpScreen;
